"use client";

import { useCallback, useEffect, useRef } from "react";
import { useParams, usePathname } from "next/navigation";
import { useNotification } from "modules/notifications/hooks/useNotification";
import { useToast } from "modules/common/components/toast/useToast";
import { useAuth } from "@clerk/nextjs";
import { truncateText } from "utils/utils";
import { Notification } from "modules/chat/model/chat";
import { UserActivityAvatar } from "modules/users/components/UserActivityAvatar";
import {
  initializeFavicon,
  resetFavicon,
  updateFaviconWithBadge,
  updatePageTitle,
} from "utils/faviconUtils";
import {
  formatNotificationMessage,
  getChatContext,
  getNotificationIcon,
  getNotificationRoute,
  getNotificationTitle,
  isChatNotification,
  isCityTradeNotification,
} from "modules/notifications/utils/notificationUtils";
import { useViewerCityMatchedRoute } from "modules/notifications/hooks/useViewerCityMatchedRoute";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { useRouter } from "@bprogress/next/app";
import { captureError } from "utils/error-sanitizer";

const TOAST_DURATION = 5000;
const AUTO_DISMISS_DELAY = TOAST_DURATION + 500;

export function useNotificationToasts() {
  const { notifications, unreadCount, markAsShown, markAsReadAsync } =
    useNotification();
  const toast = useToast();
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const { isLoaded } = useAuth();

  const viewerCityMatchedRoute = useViewerCityMatchedRoute(
    notifications.some(isCityTradeNotification)
  );

  const activeToasts = useRef(new Set<string>());
  const currentChatId = params.chatId as string;
  const previousNotificationIds = useRef(new Set<string>());
  // Track notifications that are being marked as shown to prevent duplicate calls
  const pendingShown = useRef(new Set<string>());
  // Track notifications that are being marked as read to prevent retry loops
  const pendingRead = useRef(new Set<string>());

  // Initialize favicon and setup cleanup
  useEffect(() => {
    initializeFavicon();

    return () => {
      updatePageTitle(0);
      resetFavicon();
    };
  }, []);

  // Update page title and favicon when notifications change
  useEffect(() => {
    if (!isLoaded) return;

    updatePageTitle(unreadCount);
    updateFaviconWithBadge(unreadCount);
  }, [unreadCount, isLoaded]);
  useEffect(() => {
    if (!isLoaded || notifications.length === 0) return;

    previousNotificationIds.current = new Set(notifications.map((n) => n.id));
  }, [notifications, isLoaded]);
  // Auto-mark notifications as read/shown when viewing inbox
  useEffect(() => {
    if (!pathname.includes("inbox") || !currentChatId) return;

    const currentNotification = notifications.find((n) => {
      if (isChatNotification(n)) {
        const chatContext = getChatContext(n);
        return chatContext?.chatId === currentChatId;
      }
      return false;
    });
    if (!currentNotification) return;

    // Mark as shown (uses optimistic update via mutation)
    if (
      !currentNotification.shown &&
      !pendingShown.current.has(currentNotification.id)
    ) {
      pendingShown.current.add(currentNotification.id);
      markAsShown(currentNotification.id);
    }

    // Mark as read (uses optimistic update via mutation)
    if (
      currentNotification.status === "unread" &&
      !pendingRead.current.has(currentNotification.id)
    ) {
      pendingRead.current.add(currentNotification.id);
      markAsReadAsync(currentNotification.id).catch((error) => {
        captureError(error, {
          context: "useNotificationToasts.markAsRead",
          notificationId: currentNotification.id,
        });
      });
    }
  }, [pathname, notifications, currentChatId, markAsShown, markAsReadAsync]);

  const getNotificationRouteCallback = useCallback(
    (notification: Notification): string => {
      return getNotificationRoute(notification, viewerCityMatchedRoute);
    },
    [viewerCityMatchedRoute]
  );

  const getNotificationIconCallback = useCallback(
    (notification: Notification) => {
      return getNotificationIcon(notification, "toast", ICON_SIZE);
    },
    []
  );

  const getNotificationTitleCallback = useCallback(
    (notification: Notification): string => {
      return getNotificationTitle(notification, "detailed");
    },
    []
  );

  const getNotificationDescription = useCallback(
    (notification: Notification): string => {
      if (isChatNotification(notification)) {
        return truncateText(notification.message, 100);
      }
      return truncateText(formatNotificationMessage(notification), 100);
    },
    []
  );

  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      try {
        // Remove from active toasts tracking
        activeToasts.current.delete(notification.id);
        pendingShown.current.add(notification.id);

        // Mark as read using mutation (has optimistic updates, handles invalidation)
        if (
          notification.status === "unread" &&
          !pendingRead.current.has(notification.id)
        ) {
          pendingRead.current.add(notification.id);
          await markAsReadAsync(notification.id);
        }

        router.push(getNotificationRouteCallback(notification));
      } catch (error) {
        captureError(error, {
          context: "useNotificationToasts.handleNotificationClick",
          notificationId: notification.id,
        });
      }
    },
    [markAsReadAsync, router, getNotificationRouteCallback]
  );

  const handleNotificationDismiss = useCallback(
    (notificationId: string) => {
      // Prevent duplicate calls
      if (pendingShown.current.has(notificationId)) {
        activeToasts.current.delete(notificationId);
        return;
      }

      pendingShown.current.add(notificationId);
      activeToasts.current.delete(notificationId);

      // Use mutation with optimistic updates - no need to manually invalidate
      markAsShown(notificationId);
    },
    [markAsShown]
  );

  // Show toast notifications
  useEffect(() => {
    if (!isLoaded) return;

    const toastableNotifications = notifications.filter((n) => {
      // Basic criteria for all notification types
      // Also check pendingShown to prevent showing toasts for notifications being processed
      const basicCriteria =
        n.status === "unread" &&
        !n.shown &&
        !activeToasts.current.has(n.id) &&
        !pendingShown.current.has(n.id);

      // For chat notifications, exclude if we're viewing that chat
      if (isChatNotification(n)) {
        const chatContext = getChatContext(n);
        return basicCriteria && chatContext?.chatId !== currentChatId;
      }

      // For trade notifications, always show (they don't have the concept of "current" context)
      return basicCriteria;
    });

    toastableNotifications.forEach((notification) => {
      activeToasts.current.add(notification.id);

      const notificationTitle = getNotificationTitleCallback(notification);
      const notificationDescription = getNotificationDescription(notification);
      const notificationIcon = isChatNotification(notification) ? (
        <UserActivityAvatar
          userId={notification.sender.id}
          initialStatus={notification.sender.activityStatus}
          resolveActivityStatus={false}
          preferredAvatar={notification.sender.preferredAvatar}
          username={notification.sender.username}
          size="md"
          alt={`${notification.sender.username} avatar`}
        />
      ) : (
        getNotificationIconCallback(notification)
      );

      toast.message(notificationTitle, {
        id: notification.id,
        description: notificationDescription,
        icon: notificationIcon,
        duration: TOAST_DURATION,
        action: {
          label: "View",
          onClick: () => handleNotificationClick(notification),
        },
        onDismiss: () => handleNotificationDismiss(notification.id),
      });

      // Auto-cleanup and mark as shown after toast duration
      setTimeout(() => {
        activeToasts.current.delete(notification.id);
        // Only mark as shown if not already pending (prevents duplicate calls)
        if (!pendingShown.current.has(notification.id)) {
          pendingShown.current.add(notification.id);
          markAsShown(notification.id);
        }
      }, AUTO_DISMISS_DELAY);
    });
  }, [
    notifications,
    isLoaded,
    currentChatId,
    toast,
    handleNotificationClick,
    handleNotificationDismiss,
    getNotificationTitleCallback,
    getNotificationDescription,
    getNotificationIconCallback,
    markAsShown,
  ]);

  // Cleanup stale toast and pending references
  useEffect(() => {
    const currentIds = new Set(notifications.map((n) => n.id));

    // Remove references to notifications that no longer exist or are now
    // shown/read (e.g. handled in another tab) - and dismiss the toast if
    // it's still on screen here, rather than waiting out its own timer.
    activeToasts.current.forEach((id) => {
      const notification = notifications.find((n) => n.id === id);
      const handledElsewhere =
        notification?.shown || notification?.status === "read";
      if (!currentIds.has(id) || handledElsewhere) {
        if (handledElsewhere) toast.dismiss(id);
        activeToasts.current.delete(id);
      }
    });

    // Clean up pendingShown for notifications that are now shown or no longer exist
    pendingShown.current.forEach((id) => {
      const notification = notifications.find((n) => n.id === id);
      if (!currentIds.has(id) || notification?.shown) {
        pendingShown.current.delete(id);
      }
    });

    // Clean up pendingRead for notifications that are now read or no longer exist
    pendingRead.current.forEach((id) => {
      const notification = notifications.find((n) => n.id === id);
      if (!currentIds.has(id) || notification?.status === "read") {
        pendingRead.current.delete(id);
      }
    });
  }, [notifications, toast]);
}
